import React, { useState } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';

/**
 * ====================================================================
 * RATING STARS COMPONENT
 * ====================================================================
 * Modern, animasyonlu yıldız puanlama component'i
 * - İki mod: readonly (gösterim) ve interactive (seçim)
 * - Hover efektleri
 * - Smooth animations
 * - CheckoutPage tasarım dili
 * ====================================================================
 */

const RatingStars = ({ 
  rating = 0, 
  onChange = null, 
  size = 'md', // sm, md, lg
  showCount = false, 
  reviewCount = 0,
  readonly = false,
  className = ''
}) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  const isInteractive = !readonly && onChange !== null;

  // Size mapping
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8'
  };

  const iconSize = sizeClasses[size] || sizeClasses.md;

  const handleClick = (value) => {
    if (isInteractive && onChange) {
      onChange(value);
    }
  };

  const handleMouseEnter = (value) => {
    if (isInteractive) {
      setHoverRating(value);
    }
  };

  const handleMouseLeave = () => {
    if (isInteractive) {
      setHoverRating(0);
    }
  };

  const displayRating = hoverRating || rating;

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {/* Stars */}
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => {
          const isFilled = star <= displayRating;
          const isHovered = isInteractive && star <= hoverRating;
          
          return (
            <button
              key={star}
              type="button"
              onClick={() => handleClick(star)}
              onMouseEnter={() => handleMouseEnter(star)}
              onMouseLeave={handleMouseLeave}
              disabled={!isInteractive}
              className={`
                ${isInteractive ? 'cursor-pointer' : 'cursor-default'}
                transition-all duration-200
                ${isInteractive && 'hover:scale-110 active:scale-95'}
                ${!isInteractive && 'pointer-events-none'}
              `}
              aria-label={`${star} yıldız`}
            >
              {isFilled ? (
                <StarIcon 
                  className={`
                    ${iconSize}
                    ${isHovered 
                      ? 'text-amber-400' 
                      : 'text-amber-500'
                    }
                    transition-colors duration-200
                    drop-shadow-sm
                  `}
                />
              ) : (
                <StarOutlineIcon 
                  className={`
                    ${iconSize}
                    ${isInteractive 
                      ? 'text-gray-300 hover:text-amber-300' 
                      : 'text-gray-300'
                    }
                    transition-colors duration-200
                  `}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Rating Number & Count */}
      {showCount && (
        <div className="flex items-center gap-1.5 ml-1">
          <span className="text-sm font-semibold text-gray-700">
            {rating.toFixed(1)}
          </span>
          {reviewCount > 0 && (
            <span className="text-sm text-gray-500">
              ({reviewCount} değerlendirme)
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default RatingStars;

// ====================================================================
// RATING DISTRIBUTION CHART COMPONENT
// ====================================================================
export const RatingDistribution = ({ distribution = {} }) => {
  // distribution format: { 5: 45, 4: 23, 3: 10, 2: 5, 1: 2 }
  const total = Object.values(distribution).reduce((sum, count) => sum + count, 0);
  
  if (total === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        Henüz değerlendirme yok
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {[5, 4, 3, 2, 1].map((star) => {
        const count = distribution[star] || 0;
        const percentage = total > 0 ? (count / total) * 100 : 0;

        return (
          <div key={star} className="flex items-center gap-3">
            {/* Star Label */}
            <div className="flex items-center gap-1 w-16">
              <span className="text-sm font-medium text-gray-700">{star}</span>
              <StarIcon className="w-4 h-4 text-amber-500" />
            </div>

            {/* Progress Bar */}
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${percentage}%` }}
              />
            </div>

            {/* Count */}
            <span className="text-sm text-gray-600 w-12 text-right">
              {count}
            </span>
          </div>
        );
      })}
    </div>
  );
};
